// # Chapter Database Component
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../auth/state';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import DbManager from './db_manager';
import DbStats from './db_stats';
import './db_chapter.css';
import { MdOpenInNew } from 'react-icons/md';
import DbTaskPopup from './db_task_popup';

// # Import libraries for Excel/CSV parsing
// We're not importing them at the top level to avoid circular dependencies
// They will be dynamically imported when needed

const DbChapter = () => {
  const { step, chapterId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  
  // # Add state for import raw functionality
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // # Add sort state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // # Define source options
  const sourceOptions = [
    { value: '', label: 'Select source...' },
    { value: 'BnB', label: 'BnB' },
    { value: 'BootCamp', label: 'BootCamp' },
    { value: 'Dirty Medicine', label: 'Dirty Medicine' },
    { value: 'Physeo', label: 'Physeo' },
    { value: 'Pathoma', label: 'Pathoma' },
    { value: 'Husseiny', label: 'Husseiny' },
    { value: 'Ta7', label: 'Ta7' },
    { value: 'Sketchy Micro', label: 'Sketchy Micro' },
    { value: 'Picorize', label: 'Picorize' },
    { value: 'Mehlman', label: 'Mehlman' },
  ];

  // # Add new state for columns visibility and filter
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    task_id: true,
    step: true,
    chapter: true,
    task_name: true,
    source: true,
    plan: true,
    day: true,
    duration: true,
    type: true
  });

  // # Add filter state
  const [filters, setFilters] = useState({
    plan: '',
    type: '',
    day: '',
    duration: '',
    source: ''  // # Added source filter
  });

  // # Add state for clear all confirmation
  const [showClearAllModal, setShowClearAllModal] = useState(false);

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Load initial data
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const result = await DbManager.loadChapterTasks(step, chapterId);
        if (result.success) {
          // If there's data, use it; otherwise, use initial empty rows
          setRows(result.data.length > 0 ? result.data : initialRows);
        } else {
          console.error('Error loading tasks:', result.error);
          setRows(initialRows);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setRows(initialRows);
      }
    };

    loadData();
  }, [step, chapterId]);

  // # Generate a new task ID
  const generateTaskId = () => {
    // Format chapter name: lowercase, remove spaces and special characters
    const formattedChapter = chapterId.toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    
    // Generate unique suffix (9 characters)
    const uniqueSuffix = Math.random().toString(36).substr(2, 9);
    
    return `task_${formattedChapter}_${uniqueSuffix}`;
  };

  // # Create a new empty row
  const createEmptyRow = () => ({
    task_id: generateTaskId(),
    step: step,
    chapter: chapterId,
    task_name: '',
    source: '',
    plan: '',
    day: '',
    duration: '',
    type: ''
  });

  // # Generate initial rows
  const initialRows = [
    createEmptyRow(),
    createEmptyRow(),
    createEmptyRow()
  ];

  const [rows, setRows] = useState(initialRows);

  // # Handle input change
  const handleInputChange = (taskId, field, value) => {
    if (!isEditing) return;
    
    setRows(prevRows =>
      prevRows.map(row =>
        row.task_id === taskId
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  // # Handle delete task
  const handleDeleteTask = (taskId) => {
    if (!isEditing) return;
    setDeleteTaskId(taskId);
  };

  // # Confirm delete task
  const confirmDelete = () => {
    setRows(prevRows => prevRows.filter(row => row.task_id !== deleteTaskId));
    setDeleteTaskId(null);
  };

  // # Cancel delete
  const cancelDelete = () => {
    setDeleteTaskId(null);
  };

  // # Handle add task
  const handleAddTask = () => {
    if (!isEditing) return;
    
    setRows(prevRows => [...prevRows, createEmptyRow()]);
  };

  // # Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // # Filter out empty rows
      const nonEmptyRows = rows.filter(row => 
        row.task_name || row.source || row.plan || row.day || row.duration || row.type
      );

      // # Save to database using DbManager
      const result = await DbManager.saveChapterTasks(step, chapterId, nonEmptyRows, isAdmin);
      
      if (result.success) {
        setIsEditing(false);
        // # Reload tasks to get fresh data
        const loadResult = await DbManager.loadChapterTasks(step, chapterId);
        if (loadResult.success) {
          setRows(loadResult.data);
        }
      } else {
        console.error('Error saving tasks:', result.error);
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle cancel edit
  const handleCancelEdit = async () => {
    // # Reload original data
    try {
      const result = await DbManager.loadChapterTasks(step, chapterId);
      if (result.success) {
        setRows(result.data.length > 0 ? result.data : initialRows);
      }
    } catch (error) {
      console.error('Error reloading data:', error);
    }
    setIsEditing(false);
  };

  // # Handle column visibility toggle
  const handleColumnToggle = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // # Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // # Apply filters to rows
  const applyFilters = (rows) => {
    return rows.filter(row => {
      return Object.entries(filters).every(([field, value]) => {
        if (!value) return true;
        if (field === 'day' || field === 'duration') {
          return row[field].toString() === value;
        }
        if (field === 'source') {
          return row[field] === value;
        }
        return row[field].toLowerCase().includes(value.toLowerCase());
      });
    });
  };

  // # Add sort handler
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // # Sort function
  const sortRows = (rows) => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Handle numeric fields
      if (['day', 'duration'].includes(sortConfig.key)) {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // # Filter rows based on search query and filters, then sort
  const filteredRows = sortRows(applyFilters(rows.filter(row => {
    const searchLower = searchQuery.toLowerCase();
    return (
      row.task_id.toLowerCase().includes(searchLower) ||
      row.task_name.toLowerCase().includes(searchLower) ||
      row.source.toLowerCase().includes(searchLower) ||
      row.type.toLowerCase().includes(searchLower)
    );
  })));

  // # State for form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({
    task_name: '',
    source: '',
    plan: '',
    day: '',
    duration: '',
    type: ''
  });

  // # Handle form input change
  const handleFormInputChange = (field, value) => {
    setNewTaskForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // # Handle form submit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const newTask = {
      ...newTaskForm,
      task_id: generateTaskId(),
      step: step,
      chapter: chapterId
    };
    setRows(prev => [...prev, newTask]);
    setShowFormModal(false);
    setNewTaskForm({
      task_name: '',
      source: '',
      plan: '',
      day: '',
      duration: '',
      type: ''
    });
  };

  // # Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      // # Automatically detect file type
      const fileType = file.name.split('.').pop().toLowerCase();
      setImportType(fileType);
    }
  };

  // # Parse Excel file
  const parseExcel = useCallback(async (file) => {
    try {
      // # Dynamically import XLSX
      const XLSX = await import('xlsx');
      
      // # Read file as array buffer
      const buffer = await file.arrayBuffer();
      
      // # Parse workbook
      const workbook = XLSX.read(buffer, { type: 'array' });
      
      // # Get first worksheet
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // # Convert to JSON
      return XLSX.utils.sheet_to_json(worksheet);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      throw new Error(`Failed to parse Excel file: ${error.message}`);
    }
  }, []);

  // # Parse CSV file
  const parseCsv = useCallback(async (file) => {
    try {
      // # Dynamically import Papa Parse
      const Papa = await import('papaparse');
      
      // # Read file as text
      const text = await file.text();
      
      // # Parse CSV
      return new Promise((resolve, reject) => {
        Papa.parse(text, {
          header: true,
          complete: (results) => {
            if (results.errors && results.errors.length > 0) {
              reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
            } else {
              resolve(results.data);
            }
          },
          error: (error) => {
            reject(new Error(`CSV parsing error: ${error.message}`));
          }
        });
      });
    } catch (error) {
      console.error('Error parsing CSV file:', error);
      throw new Error(`Failed to parse CSV file: ${error.message}`);
    }
  }, []);

  // # Process imported data
  const processImportedData = useCallback((rawData) => {
    // # Generate task IDs and add required fields
    return rawData.map(row => {
      // # Format chapter name for task ID
      const formattedChapter = chapterId.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      
      // # Generate unique suffix
      const uniqueSuffix = Math.random().toString(36).substr(2, 9);
      
      // # Create task ID
      const task_id = `task_${formattedChapter}_${uniqueSuffix}`;
      
      // # Return processed row
      return {
        ...row,
        task_id,
        step,
        chapter: chapterId
      };
    });
  }, [step, chapterId]);

  // # Handle import submit
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResult(null);
    
    try {
      // # Check file size (limit to 5MB)
      if (importFile.size > 5 * 1024 * 1024) {
        throw new Error('File size exceeds 5MB limit. Please upload a smaller file.');
      }
      
      // # Check file type
      const fileType = importFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(fileType)) {
        throw new Error('Unsupported file type. Please upload an Excel (.xlsx) or CSV file.');
      }
      
      // # Parse file based on type
      let rawData = [];
      if (fileType === 'csv') {
        rawData = await parseCsv(importFile);
      } else {
        rawData = await parseExcel(importFile);
      }
      
      // # Process the data
      const processedData = processImportedData(rawData);
      
      // # Save to database
      const result = await DbManager.saveChapterTasks(
        step, 
        chapterId, 
        processedData, 
        user?.uid || 'unknown'
      );
      
      if (result.success) {
        setImportResult({
          success: true,
          rowsImported: processedData.length
        });
        
        // # Reload tasks
        const loadResult = await DbManager.loadChapterTasks(step, chapterId);
        if (loadResult.success) {
          setRows(loadResult.data);
        }
        
        // # Close modal after delay
        setTimeout(() => {
          setShowImportModal(false);
          setImportFile(null);
          setImportType('');
          setImportResult(null);
        }, 3000);
      } else {
        throw new Error(result.error || 'Failed to save imported data');
      }
    } catch (error) {
      console.error('Error importing data:', error);
      setImportResult({
        success: false,
        error: error.message || 'An unknown error occurred during import'
      });
    } finally {
      setIsImporting(false);
    }
  };

  // # Handle clear all tasks
  const handleClearAllTasks = () => {
    setRows([]);
    setShowClearAllModal(false);
  };

  const [selectedTask, setSelectedTask] = useState(null);
  
  // # Handle task popup open
  const handleTaskPopupOpen = (task) => {
    setSelectedTask(task);
  };

  // # Handle task popup close
  const handleTaskPopupClose = () => {
    setSelectedTask(null);
  };

  // # Handle task popup save
  const handleTaskPopupSave = (editedTask) => {
    setRows(prevRows =>
      prevRows.map(row =>
        row.task_id === editedTask.task_id ? editedTask : row
      )
    );
    setSelectedTask(null);
  };

  return (
    <div className="mstr_db_chp_db_chapter">
      <SharedHeader />
      
      <main className="mstr_db_chp_db_chapter_content">
        <div className="mstr_db_chp_db_chapter_header">
          <button 
            className="mstr_db_chp_back-button"
            onClick={() => navigate(`/admin/content/${step}`)}
          >
            <i className="fas fa-arrow-left"></i> Back to {step.toUpperCase()}
          </button>
          <h1>{chapterId} Database</h1>
        </div>

        <DbStats rows={rows} />

        {/* Controls Bar */}
        <div className="mstr_db_chp_controls_container">
          <div className="mstr_db_chp_search_container">
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mstr_db_chp_search_input"
            />
          </div>
          
          <div className="mstr_db_chp_buttons_container">
            {/* Add Clear All button */}
            {isEditing && (
              <button 
                className="mstr_db_chp_control_button clear-all"
                onClick={() => setShowClearAllModal(true)}
              >
                <i className="fas fa-trash-alt"></i>
                Clear All
              </button>
            )}
            
            {/* Add Import Raw button */}
            <button 
              className="mstr_db_chp_control_button import"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-import"></i>
              Import Raw
            </button>
            
            <button 
              className="mstr_db_chp_control_button columns"
              onClick={() => setShowColumnsModal(true)}
            >
              <i className="fas fa-columns"></i>
              Columns
            </button>
            
            <button 
              className="mstr_db_chp_control_button filter"
              onClick={() => setShowFilterModal(true)}
            >
              <i className="fas fa-filter"></i>
              Filter
            </button>

            {isEditing ? (
              <>
                <button 
                  className="mstr_db_chp_control_button cancel"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button 
                  className="mstr_db_chp_control_button save"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <i className="fas fa-save"></i>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button 
                className="mstr_db_chp_control_button edit"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit"></i>
                Edit Table
              </button>
            )}
          </div>
        </div>

        {/* Table Container */}
        <div className="mstr_db_chp_table_container">
          <table className="mstr_db_chp_table">
            <thead>
              <tr>
                {isEditing && <th data-column="delete">Delete</th>}
                {visibleColumns.task_id && (
                  <th 
                    data-column="task_id"
                    onClick={() => handleSort('task_id')}
                    className={`sortable ${sortConfig.key === 'task_id' ? sortConfig.direction : ''}`}
                  >
                    Task ID {sortConfig.key === 'task_id' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.step && (
                  <th 
                    data-column="step"
                    onClick={() => handleSort('step')}
                    className={`sortable ${sortConfig.key === 'step' ? sortConfig.direction : ''}`}
                  >
                    Step {sortConfig.key === 'step' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.chapter && (
                  <th 
                    data-column="chapter"
                    onClick={() => handleSort('chapter')}
                    className={`sortable ${sortConfig.key === 'chapter' ? sortConfig.direction : ''}`}
                  >
                    Chapter {sortConfig.key === 'chapter' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.task_name && (
                  <th 
                    data-column="task_name"
                    onClick={() => handleSort('task_name')}
                    className={`sortable ${sortConfig.key === 'task_name' ? sortConfig.direction : ''}`}
                  >
                    Task Name {sortConfig.key === 'task_name' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.source && (
                  <th 
                    data-column="source"
                    onClick={() => handleSort('source')}
                    className={`sortable ${sortConfig.key === 'source' ? sortConfig.direction : ''}`}
                  >
                    Source {sortConfig.key === 'source' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.plan && (
                  <th 
                    data-column="plan"
                    onClick={() => handleSort('plan')}
                    className={`sortable ${sortConfig.key === 'plan' ? sortConfig.direction : ''}`}
                  >
                    Plan {sortConfig.key === 'plan' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.day && (
                  <th 
                    data-column="day"
                    onClick={() => handleSort('day')}
                    className={`sortable ${sortConfig.key === 'day' ? sortConfig.direction : ''}`}
                  >
                    Day {sortConfig.key === 'day' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.duration && (
                  <th 
                    data-column="duration"
                    onClick={() => handleSort('duration')}
                    className={`sortable ${sortConfig.key === 'duration' ? sortConfig.direction : ''}`}
                  >
                    Duration (min) {sortConfig.key === 'duration' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
                {visibleColumns.type && (
                  <th 
                    data-column="type"
                    onClick={() => handleSort('type')}
                    className={`sortable ${sortConfig.key === 'type' ? sortConfig.direction : ''}`}
                  >
                    Type {sortConfig.key === 'type' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} />
                    )}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(row => (
                <tr key={row.task_id} className={isEditing ? 'editing' : ''}>
                  {isEditing && (
                    <td data-column="delete">
                      <button
                        className="mstr_db_chp_delete-button"
                        onClick={() => handleDeleteTask(row.task_id)}
                        title="Delete task"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  )}
                  {visibleColumns.task_id && <td data-column="task_id">{row.task_id}</td>}
                  {visibleColumns.step && <td data-column="step">{row.step}</td>}
                  {visibleColumns.chapter && <td data-column="chapter">{row.chapter}</td>}
                  {visibleColumns.task_name && (
                    <td data-column="task_name" className="mstr_db_chp_task_name_cell">
                      <div className="mstr_db_chp_task_name_wrapper">
                        <input
                          type="text"
                          value={row.task_name}
                          onChange={(e) => handleInputChange(row.task_id, 'task_name', e.target.value)}
                          disabled={!isEditing}
                          className="mstr_db_chp_table_input"
                        />
                        <button
                          className="mstr_db_chp_expand_button"
                          onClick={() => handleTaskPopupOpen(row)}
                          title="View/Edit task details"
                        >
                          <MdOpenInNew />
                        </button>
                      </div>
                    </td>
                  )}
                  {visibleColumns.source && (
                    <td data-column="source" data-source={row.source}>
                      <select
                        value={row.source}
                        onChange={(e) => handleInputChange(row.task_id, 'source', e.target.value)}
                        disabled={!isEditing}
                        className="mstr_db_chp_table_select"
                      >
                        {sourceOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  {visibleColumns.plan && (
                    <td data-column="plan" data-plan={row.plan}>
                      <select
                        value={row.plan}
                        onChange={(e) => handleInputChange(row.task_id, 'plan', e.target.value)}
                        disabled={!isEditing}
                        className="mstr_db_chp_table_select"
                      >
                        <option value="">Select...</option>
                        <option value="Plan A">Plan A</option>
                        <option value="Plan B">Plan B</option>
                        <option value="Plan C">Plan C</option>
                      </select>
                    </td>
                  )}
                  {visibleColumns.day && (
                    <td data-column="day">
                      <input
                        type="number"
                        value={row.day}
                        onChange={(e) => handleInputChange(row.task_id, 'day', e.target.value)}
                        disabled={!isEditing}
                        className="mstr_db_chp_table_input"
                        style={{ minWidth: '40px', width: 'auto' }}
                      />
                    </td>
                  )}
                  {visibleColumns.duration && (
                    <td data-column="duration">
                      <input
                        type="number"
                        value={row.duration}
                        onChange={(e) => handleInputChange(row.task_id, 'duration', e.target.value)}
                        disabled={!isEditing}
                        className="mstr_db_chp_table_input"
                        style={{ minWidth: '60px', width: 'auto' }}
                      />
                    </td>
                  )}
                  {visibleColumns.type && (
                    <td data-column="type">
                      <input
                        type="text"
                        value={row.type}
                        onChange={(e) => handleInputChange(row.task_id, 'type', e.target.value)}
                        disabled={!isEditing}
                        className="mstr_db_chp_table_input"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Task Buttons */}
        {isEditing && (
          <div className="mstr_db_chp_task-buttons">
            <button 
              className="mstr_db_chp_add-task-button"
              onClick={handleAddTask}
            >
              <i className="fas fa-plus"></i>
              Add Empty Row
            </button>
            <button 
              className="mstr_db_chp_add-task-form-button"
              onClick={() => setShowFormModal(true)}
            >
              <i className="fas fa-file-alt"></i>
              Add Task As Form
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTaskId && (
          <div className="mstr_db_chp_delete-modal">
            <div className="mstr_db_chp_delete-modal-content">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this task?</p>
              <div className="mstr_db_chp_delete-modal-buttons">
                <button className="mstr_db_chp_modal-button cancel" onClick={cancelDelete}>
                  Cancel
                </button>
                <button className="mstr_db_chp_modal-button confirm" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Columns Modal */}
        {showColumnsModal && (
          <div className="mstr_db_chp_columns-modal">
            <div className="mstr_db_chp_modal_content">
              <h3>Visible Columns</h3>
              <div className="mstr_db_chp_columns-list">
                {Object.keys(visibleColumns).map(column => (
                  <div key={column} className="mstr_db_chp_column-toggle">
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleColumns[column]}
                        onChange={() => handleColumnToggle(column)}
                      />
                      {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                  </div>
                ))}
              </div>
              <div className="mstr_db_chp_modal-buttons">
                <button 
                  className="mstr_db_chp_modal_button"
                  onClick={() => setShowColumnsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="mstr_db_chp_filter-modal">
            <div className="mstr_db_chp_modal_content">
              <div className="mstr_db_chp_modal_header">
                <h3>Filter Tasks</h3>
                <button 
                  className="mstr_db_chp_modal_close"
                  onClick={() => setShowFilterModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="mstr_db_chp_filter-form">
                <div className="mstr_db_chp_filter-group">
                  <label>Source:</label>
                  <select
                    value={filters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                  >
                    <option value="">All</option>
                    {sourceOptions.filter(option => option.value).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mstr_db_chp_filter-group">
                  <label>Plan:</label>
                  <select
                    value={filters.plan}
                    onChange={(e) => handleFilterChange('plan', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Plan A">Plan A</option>
                    <option value="Plan B">Plan B</option>
                    <option value="Plan C">Plan C</option>
                  </select>
                </div>
                <div className="mstr_db_chp_filter-group">
                  <label>Type:</label>
                  <input
                    type="text"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    placeholder="Filter by type..."
                  />
                </div>
                <div className="mstr_db_chp_filter-group">
                  <label>Day:</label>
                  <input
                    type="number"
                    value={filters.day}
                    onChange={(e) => handleFilterChange('day', e.target.value)}
                    placeholder="Filter by day..."
                  />
                </div>
                <div className="mstr_db_chp_filter-group">
                  <label>Duration:</label>
                  <input
                    type="number"
                    value={filters.duration}
                    onChange={(e) => handleFilterChange('duration', e.target.value)}
                    placeholder="Filter by duration..."
                  />
                </div>
              </div>
              <div className="mstr_db_chp_modal-buttons">
                <button 
                  className="mstr_db_chp_modal_button cancel"
                  onClick={() => {
                    setFilters({
                      plan: '',
                      type: '',
                      day: '',
                      duration: '',
                      source: ''
                    });
                  }}
                >
                  Clear Filters
                </button>
                <button 
                  className="mstr_db_chp_modal_button"
                  onClick={() => setShowFilterModal(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Task Form Modal */}
        {showFormModal && (
          <div className="mstr_db_chp_task-form-modal">
            <div className="mstr_db_chp_task-form-content">
              <h3>Add New Task</h3>
              <form onSubmit={handleFormSubmit}>
                <div className="mstr_db_chp_form-group">
                  <label>Task Name:</label>
                  <input
                    type="text"
                    value={newTaskForm.task_name}
                    onChange={(e) => handleFormInputChange('task_name', e.target.value)}
                    placeholder="Enter task name..."
                    required
                  />
                </div>
                <div className="mstr_db_chp_form-group">
                  <label>Source:</label>
                  <select
                    value={newTaskForm.source}
                    onChange={(e) => handleFormInputChange('source', e.target.value)}
                  >
                    {sourceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mstr_db_chp_form-group">
                  <label>Plan:</label>
                  <select
                    value={newTaskForm.plan}
                    onChange={(e) => handleFormInputChange('plan', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Plan A">Plan A</option>
                    <option value="Plan B">Plan B</option>
                    <option value="Plan C">Plan C</option>
                  </select>
                </div>
                <div className="mstr_db_chp_form-group">
                  <label>Day:</label>
                  <input
                    type="number"
                    value={newTaskForm.day}
                    onChange={(e) => handleFormInputChange('day', e.target.value)}
                    placeholder="Enter day..."
                  />
                </div>
                <div className="mstr_db_chp_form-group">
                  <label>Duration (min):</label>
                  <input
                    type="number"
                    value={newTaskForm.duration}
                    onChange={(e) => handleFormInputChange('duration', e.target.value)}
                    placeholder="Enter duration..."
                  />
                </div>
                <div className="mstr_db_chp_form-group">
                  <label>Type:</label>
                  <input
                    type="text"
                    value={newTaskForm.type}
                    onChange={(e) => handleFormInputChange('type', e.target.value)}
                    placeholder="Enter type..."
                  />
                </div>
                <div className="mstr_db_chp_form-buttons">
                  <button 
                    type="button" 
                    className="mstr_db_chp_cancel-button"
                    onClick={() => setShowFormModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="mstr_db_chp_submit-button"
                  >
                    Add Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Raw Modal */}
        {showImportModal && (
          <div className="mstr_db_chp_import-modal">
            <div className="mstr_db_chp_modal_content">
              <h3>Import Raw Data</h3>
              <p className="mstr_db_chp_import-instructions">
                Upload an Excel (.xlsx) or CSV file to import tasks.
                <br />
                <strong>Note:</strong> The following fields will be automatically added:
                <ul>
                  <li>task_id</li>
                  <li>step</li>
                  <li>chapter</li>
                </ul>
              </p>
              
              <div className="mstr_db_chp_import-requirements">
                <h4>Requirements:</h4>
                <ul>
                  <li>File must be Excel (.xlsx/.xls) or CSV format</li>
                  <li>Maximum file size: 5MB</li>
                  <li>First row must contain column headers</li>
                  <li>Recommended columns: task_name, source, plan, day, duration, type</li>
                </ul>
              </div>
              
              <form onSubmit={handleImportSubmit}>
                <div className="mstr_db_chp_import-file-container">
                  <label className="mstr_db_chp_import-file-label">
                    <span>Select File:</span>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="mstr_db_chp_import-file-input"
                    />
                  </label>
                  {importFile && (
                    <div className="mstr_db_chp_selected-file">
                      <span>Selected: {importFile.name}</span>
                      <span className="mstr_db_chp_file-size">
                        ({(importFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>
                
                {importResult && (
                  <div className={`mstr_db_chp_import-result ${importResult.success ? 'success' : 'error'}`}>
                    {importResult.success ? (
                      <p>Successfully imported {importResult.rowsImported} tasks!</p>
                    ) : (
                      <p>Error: {importResult.error}</p>
                    )}
                  </div>
                )}
                
                <div className="mstr_db_chp_modal-buttons">
                  <button 
                    type="button"
                    className="mstr_db_chp_modal_button cancel"
                    onClick={() => setShowImportModal(false)}
                    disabled={isImporting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="mstr_db_chp_modal_button import"
                    disabled={!importFile || isImporting}
                  >
                    {isImporting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Importing...
                      </>
                    ) : 'Import Data'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Clear All Confirmation Modal */}
        {showClearAllModal && (
          <div className="mstr_db_chp_delete-modal">
            <div className="mstr_db_chp_delete-modal-content">
              <h3>Clear All Tasks</h3>
              <p>Are you sure you want to delete all tasks? This action cannot be undone.</p>
              <div className="mstr_db_chp_delete-modal-buttons">
                <button 
                  className="mstr_db_chp_modal-button cancel"
                  onClick={() => setShowClearAllModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="mstr_db_chp_modal-button confirm"
                  onClick={handleClearAllTasks}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedTask && (
          <DbTaskPopup
            task={selectedTask}
            onClose={handleTaskPopupClose}
            onSave={handleTaskPopupSave}
            sourceOptions={sourceOptions}
          />
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default DbChapter;
